// Search URLs. Pure, so tested. Moved from chawk-bazar / ciseco / jument (`helpers/searchUrl.js`; jument had only
// `with_search_query`, ciseco also `search_term_url` / `SEARCH_TERM_KEYS`) and ciseco `helpers/loginUrl.js#build_search_url`.

export const DEFAULT_SEARCH_PATH = "/search";

// The query keys a search term is read from (ciseco `read_search_term`); links always write `q`.
export const SEARCH_TERM_KEYS = Object.freeze(["q", "query", "search"]);

// Kept when the filters are reset (`search_term_url` with keep_filters false).
const KEEP_ON_RESET = /^(sort|utm_[a-z]+)$/;

// A path on this site: `/x`, not `//host`, a backslash, or a URL built from `undefined` / `null` (route name missing
// in the panel). Same rule as chawk-bazar / ciseco `is_valid_internal_url`.
const is_internal_url = (url) =>
	typeof url === "string" && url.startsWith("/") && !url.startsWith("//") && !url.includes("\\") && !/\b(undefined|null)\b/.test(url);

const with_trailing_slash = (path) => (path.endsWith("/") ? path : `${path}/`);

/**
 * `url` with the search term added as `q`. The backend's `seo_full_url` may already have a query
 * (`/products/x?brands=y`), so `?q=` is merged into it instead of adding a second `?`; the term is encoded
 * (`کت & شلوار`), the hash stays last, the path gets a trailing slash (`trailingSlash: true`), and an empty url means
 * the global search page. An old `q` in `url` is replaced (it used to be duplicated, and the first one won).
 * (chawk-bazar / ciseco / jument)
 */
export const with_search_query = (url, search) => {
	const term = typeof search === "string" ? search.trim() : "";
	const base = typeof url === "string" ? url.trim() : "";
	const target = base || DEFAULT_SEARCH_PATH;

	const hash_at = target.indexOf("#");
	const path_and_query = hash_at < 0 ? target : target.slice(0, hash_at);
	const hash = hash_at < 0 ? "" : target.slice(hash_at + 1);

	const query_at = path_and_query.indexOf("?");
	const path = query_at < 0 ? path_and_query : path_and_query.slice(0, query_at);
	const query = query_at < 0 ? "" : path_and_query.slice(query_at + 1);

	const kept = term ? query.split("&").filter((part) => part && part.split("=")[0] !== "q") : query.split("&").filter(Boolean);
	const merged_query = [...kept, term ? `q=${encodeURIComponent(term)}` : ""].filter(Boolean).join("&");

	return with_trailing_slash(path) + (merged_query ? `?${merged_query}` : "") + (hash ? `#${hash}` : "");
};

/**
 * Where a search result (brand, category, product) links: its `seo_full_url` with the term, or, when the backend
 * gave no usable URL (`null`, `undefined?brands=x`, an external one), the search page for `fallback_term`
 * (default: the result's title). (chawk-bazar / ciseco)
 * @param options.search_path the search page; default `/search`
 */
export const search_result_href = (item, search, fallback_term = item?.title, { search_path = DEFAULT_SEARCH_PATH } = {}) => {
	const url = item?.seo_full_url;
	return is_internal_url(url) ? with_search_query(url, search) : with_search_query(search_path, fallback_term || search);
};

/**
 * The same listing page with a new search term. The term is read on the server (h1, input, metadata, results), so
 * changing or clearing it must be a real navigation. Every term key and `page` are dropped; an empty term means none;
 * `keep_filters: false` also drops every filter except sort and utm_*. (ciseco)
 */
export const search_term_url = (pathname, search = "", { term = "", keep_filters = true } = {}) => {
	const params = new URLSearchParams(typeof search === "string" ? search.replace(/^\?/, "") : "");
	for (const key of [...params.keys()]) {
		if (SEARCH_TERM_KEYS.includes(key) || key === "page" || (!keep_filters && !KEEP_ON_RESET.test(key))) params.delete(key);
	}
	const clean_term = typeof term === "string" ? term.replace(/\s+/g, " ").trim() : "";
	if (clean_term) params.set("q", clean_term);
	const query = params.toString();
	return `${with_trailing_slash(String(pathname || `${DEFAULT_SEARCH_PATH}/`))}${query ? `?${query}` : ""}`;
};

/**
 * The search page for a term: path from `web_route_names.search` (default `/search`), always with `?q=` (even empty).
 * (ciseco `loginUrl.js`)
 */
export const build_search_url = (search_path, query) => {
	const base = `${String(search_path || DEFAULT_SEARCH_PATH).replace(/\/+$/, "")}/`;
	return `${base}?q=${encodeURIComponent(query ?? "")}`;
};

export default with_search_query;
